import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import SignaturePad from '../SignaturePad';
import { getFormText } from '../PolicyText';

const DirectDepositForm = ({ language = 'en', onSubmit, loading, disabled }) => {
  const [formData, setFormData] = useState({
    nameOfFinancialInstitution: '',
    branchCode: '',
    transitNumber: '',
    accountNumber: '',
    accountType: 'checking'
  });
  const [signature, setSignature] = useState(null);
  const [errors, setErrors] = useState({});

  const t = {
    en: {
      authAgreement: 'Authorization Agreement',
      clause1: 'I hereby authorize to initiate automatic deposits to my account at the financial institution named below. I also authorize to make withdrawals from this account in the event that a credit entry is made in error.',
      clause2: 'Further, I agree not to hold responsible for any delay or loss of funds due to incorrect or incomplete information supplied by me or by my financial institution or due to an error on the part of my financial institution in depositing funds to my account.',
      clause3: 'This agreement will remain in effect until receives a written notice of cancellation from me or my financial institution, or until I submit a new direct deposit form to the Payroll Department.',
      accountInfo: 'Account Information',
      institution: 'Name of Financial Institution',
      branchCode: 'Branch Code',
      transitNumber: 'Transit Number',
      accountNumber: 'Account Number',
      accountType: 'Account Type',
      checking: 'Checking',
      savings: 'Savings',
      signature: 'Authorized Signature (Primary)',
      save: 'Save Form',
      requiredField: 'This field is required',
      signatureRequired: 'Signature is required'
    },
    es: {
      authAgreement: 'Acuerdo de Autorización',
      clause1: 'Por la presente autorizo a iniciar depósitos automáticos en mi cuenta en la institución financiera nombrada a continuación. También autorizo a hacer retiros de esta cuenta en caso de que se realice un abono por error.',
      clause2: 'Además, acepto no responsabilizar por ningún retraso o pérdida de fondos debido a información incorrecta o incompleta proporcionada por mí o por mi institución financiera o debido a un error por parte de mi institución financiera al depositar fondos en mi cuenta.',
      clause3: 'Este acuerdo permanecerá vigente hasta que reciba un aviso por escrito de cancelación de mi parte o de mi institución financiera, o hasta que presente un nuevo formulario de depósito directo al Departamento de Nómina.',
      accountInfo: 'Información de la Cuenta',
      institution: 'Nombre de la Institución Financiera',
      branchCode: 'Código de Sucursal',
      transitNumber: 'Número de Tránsito',
      accountNumber: 'Número de Cuenta',
      accountType: 'Tipo de Cuenta',
      checking: 'Corriente',
      savings: 'Ahorros',
      signature: 'Firma Autorizada (Principal)',
      save: 'Guardar Formulario',
      requiredField: 'Este campo es obligatorio',
      signatureRequired: 'Se requiere firma'
    }
  };

  const labels = t[language] || t.en;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.nameOfFinancialInstitution.trim()) {
      newErrors.nameOfFinancialInstitution = labels.requiredField;
    }
    if (!formData.branchCode.trim()) {
      newErrors.branchCode = labels.requiredField;
    }
    if (!formData.transitNumber.trim()) {
      newErrors.transitNumber = labels.requiredField;
    }
    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = labels.requiredField;
    }
    if (!signature) {
      newErrors.signature = labels.signatureRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validate()) {
      onSubmit(formData, signature);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} autoComplete="off" data-lpignore="true" data-form-type="other">
      {/* Authorization Agreement Section */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        {labels.authAgreement}
      </Typography>
      
      <Box sx={{ pl: 0, mb: 3 }}>
        <Typography variant="body2" paragraph>
          {labels.clause1}
        </Typography>
        <Typography variant="body2" paragraph>
          {labels.clause2}
        </Typography>
        <Typography variant="body2" paragraph>
          {labels.clause3}
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Account Information Section */}
      <Typography variant="h6" gutterBottom>
        {labels.accountInfo}
      </Typography>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={labels.institution}
            name="nameOfFinancialInstitution"
            value={formData.nameOfFinancialInstitution}
            onChange={handleChange}
            required
            disabled={disabled}
            error={!!errors.nameOfFinancialInstitution}
            helperText={errors.nameOfFinancialInstitution}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={labels.branchCode}
            name="branchCode"
            value={formData.branchCode}
            onChange={handleChange}
            required
            disabled={disabled}
            error={!!errors.branchCode}
            helperText={errors.branchCode}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={labels.transitNumber}
            name="transitNumber"
            value={formData.transitNumber}
            onChange={handleChange}
            required
            disabled={disabled}
            error={!!errors.transitNumber}
            helperText={errors.transitNumber}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={labels.accountNumber}
            name="accountNumber"
            value={formData.accountNumber}
            onChange={handleChange}
            required
            disabled={disabled}
            error={!!errors.accountNumber}
            helperText={errors.accountNumber}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel>{labels.accountType}</InputLabel>
            <Select
              name="accountType"
              value={formData.accountType}
              onChange={handleChange}
              label={labels.accountType}
              disabled={disabled}
            >
              <MenuItem value="checking">{labels.checking}</MenuItem>
              <MenuItem value="savings">{labels.savings}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Signature Section */}
      <SignaturePad
        onSignature={setSignature}
        label={labels.signature}
        language={language}
      />
      {errors.signature && (
        <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
          {errors.signature}
        </Typography>
      )}

      {/* Legacy PL Hub instruction below the signature */}
      <Typography variant="body2" sx={{ mt: 2, fontWeight: 600 }}>
        {getFormText(language, 'text.directDeposit.voidedCheque')}
      </Typography>

      {/* Submit Button */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          startIcon={<SaveIcon />}
          disabled={disabled || loading}
        >
          {labels.save}
        </Button>
      </Box>
    </Box>
  );
};

export default DirectDepositForm;

